import { useEffect, useState } from 'react';

import DataTable from '../../components/table/DataTable';
import ActionMenu from '../../components/ui/ActionMenu';
import FormField from '../../components/ui/FormField';
import FormModal from '../../components/ui/FormModal';
import Modal from '../../components/ui/Modal';
import SearchBar from '../../components/ui/SearchBar';
import StatusMessage from '../../components/ui/StatusMessage';
import { useAuth } from '../../context/AuthContext';
import {
  createCategory,
  getCategories,
  softDeleteCategory,
  updateCategory,
} from '../../services/firestoreService';

const rowStyle = {
  cursor: 'default',
};

const nameCellStyle = {
  fontWeight: 600,
  color: '#0f172a',
};

const actionsCellStyle = {
  width: 72,
  textAlign: 'right',
};

const getCategoryName = (category) =>
  category?.name || category?.categoryName || '-';

const normalizeCreatedAt = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatCreatedAt = (value) => {
  const date = normalizeCreatedAt(value);

  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export default function CategoriesList() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formName, setFormName] = useState('');
  const [formError, setFormError] = useState('');
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isAdmin } = useAuth();

  const loadCategories = async () => {
    setLoading(true);
    setPageError('');

    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      setPageError('Failed to load categories.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const openAddModal = () => {
    setSelectedCategory(null);
    setFormName('');
    setFormError('');
    setIsFormModalOpen(true);
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setFormName(getCategoryName(category) === '-' ? '' : getCategoryName(category));
    setFormError('');
    setIsFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (isSubmitting) {
      return;
    }

    setIsFormModalOpen(false);
    setSelectedCategory(null);
    setFormName('');
    setFormError('');
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) {
      return;
    }

    setIsDeleteModalOpen(false);
    setSelectedCategory(null);
  };

  const resetFormModalState = () => {
    setIsFormModalOpen(false);
    setSelectedCategory(null);
    setFormName('');
    setFormError('');
  };

  const resetDeleteModalState = () => {
    setIsDeleteModalOpen(false);
    setSelectedCategory(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = formName.trim();

    if (!trimmedName) {
      setFormError('Category name is required.');
      return;
    }

    setFormError('');
    setIsSubmitting(true);

    try {
      if (selectedCategory?.id) {
        await updateCategory(selectedCategory.id, { name: trimmedName });
        setPageMessage('Category updated.');
      } else {
        await createCategory({ name: trimmedName });
        setPageMessage('Category created.');
      }

      setPageError('');
      resetFormModalState();
      await loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      setFormError('Failed to save category. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting || !selectedCategory?.id) {
      return;
    }

    setIsDeleting(true);

    try {
      await softDeleteCategory(selectedCategory.id);
      setPageMessage('Category deleted.');
      setPageError('');
      resetDeleteModalState();
      await loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      setPageError('Failed to delete category.');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredCategories = categories.filter((category) =>
    getCategoryName(category)
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
  );

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'createdAt', label: 'Created At' },
  ];

  if (isAdmin) {
    columns.push({ key: 'actions', label: '' });
  }

  const formTitle = selectedCategory ? 'Edit Category' : 'Add Category';
  const submitLabel = isSubmitting
    ? selectedCategory
      ? 'Saving...'
      : 'Creating...'
    : selectedCategory
      ? 'Save Changes'
      : 'Save Category';

  return (
    <div className="page page--compact-list">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">Categories</h1>
          <p className="page-subtitle">
            Manage category records used across suppliers and products.
          </p>
        </div>

        {isAdmin && (
          <div className="page-header__actions">
            <button type="button" className="button button-primary" onClick={openAddModal}>
              + Add Category
            </button>
          </div>
        )}
      </div>

      <StatusMessage tone="success" message={pageMessage} />
      <StatusMessage tone="error" message={pageError} />

      <section className="section">
        <div className="controls-bar">
          <div className="controls-bar__search">
            <SearchBar
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search categories by name..."
            />
          </div>
        </div>

        {loading ? <p className="page-subtitle">Loading categories...</p> : null}

        {!loading ? (
          <div className="table-wrapper list-table-wrapper">
            <DataTable
              columns={columns}
              data={filteredCategories}
              emptyMessage="No categories found."
              renderRow={(category) => (
                <tr key={category.id} style={rowStyle}>
                  <td style={nameCellStyle}>{getCategoryName(category)}</td>
                  <td>{formatCreatedAt(category.createdAt)}</td>
                  {isAdmin && (
                    <td
                      style={actionsCellStyle}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <ActionMenu
                        onEdit={() => openEditModal(category)}
                        onDelete={() => openDeleteModal(category)}
                      />
                    </td>
                  )}
                </tr>
              )}
            />
          </div>
        ) : null}
      </section>

      <FormModal
        isOpen={isFormModalOpen}
        onClose={closeFormModal}
        onSubmit={handleSubmit}
        title={formTitle}
        submitLabel={submitLabel}
        submitDisabled={isSubmitting}
        cancelDisabled={isSubmitting}
        errorMessage={formError}
      >
        <FormField
          name="name"
          label="Name"
          required
          value={formName}
          onChange={(value) => {
            setFormName(value);
            if (formError) {
              setFormError('');
            }
          }}
        />
      </FormModal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Category"
        description={`Are you sure you want to delete ${
          getCategoryName(selectedCategory) === '-'
            ? 'this category'
            : getCategoryName(selectedCategory)
        }?`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
      />
    </div>
  );
}
